import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { matchPath } from 'react-router-dom'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import {
  GALLERY_IMAGE_PATH_PATTERN,
  IMAGE_PATH_PATTERN,
  STUDIO_PATH,
  buildImagePath,
  hasImageEditParam,
} from 'Pods/ThreeDStudio/constants'
import useImageDownload from 'Pods/ThreeDStudio/Hooks/useImageDownload'
import useImageEdit from 'Pods/ThreeDStudio/Hooks/useImageEdit'
import useImageGenerate3d from 'Pods/ThreeDStudio/Hooks/useImageGenerate3d'
import useImageRegenerate from 'Pods/ThreeDStudio/Hooks/useImageRegenerate'
import ImageModal from 'Pods/ThreeDStudio/Shared/ImageModal/ImageModal'
import { toModalImage } from 'Pods/ThreeDStudio/Shared/ImageModal/Utils/toModalImage'
import { openFullImageEditor } from 'Pods/ThreeDStudio/Store/actions/ui'

import useLibraryImageNavigation from './useLibraryImageNavigation'

const EDIT_OPTIONS = { buildPath: buildImagePath }

// Opens the image a shared /ai-image/:slug URL names: in the image modal the community gallery
// uses too, or in the full-width editor when the URL carries `edit=true`.
// /ai-image-generator/images/:slug?edit=true opens the editor too, while without `edit` the
// gallery shows that image in its own modal. An unknown or private slug never gets here, as
// ThreeDStudioController redirects it away. Mounted outside the routes, so it reads the slug off
// the pathname and renders nothing on any other page.
function ImageDeepLink() {
  const location = useLocation()
  const pathname = location?.pathname ?? ''
  const imageSlug = matchPath(IMAGE_PATH_PATTERN, pathname)?.params?.slug ?? null
  const gallerySlug = matchPath(GALLERY_IMAGE_PATH_PATTERN, pathname)?.params?.slug ?? null
  const isEditRequested = hasImageEditParam(location?.search)
  const slug = imageSlug ?? (isEditRequested ? gallerySlug : null)
  const navigate = useNavigate()
  const { getData } = useApiGet()
  const [loaded, setLoaded] = useState(null)

  useEffect(() => {
    if (!slug) return

    getData({
      path: `/api/internal/generator/images/${slug}`,
      onSuccess: (response) => {
        setLoaded({ slug, attributes: response?.data?.data?.attributes ?? null })
      },
    })
  }, [getData, slug])

  // Keyed on the slug so a stale image never shows while the next one loads.
  const attributes = loaded?.slug === slug ? loaded.attributes : null
  const runId = attributes?.id
  const loadedImage = useMemo(() => toModalImage(attributes), [attributes])

  const {
    hasPrevious, hasNext, previous: handlePrevious, next: handleNext, listedAttributes,
  } = useLibraryImageNavigation(isEditRequested ? null : imageSlug)

  // Stepping between library images would otherwise unmount the modal while the next image's
  // payload loads, blinking it shut. Its library entry is already in hand, so it stands in
  // until then. The library only lists the viewer's own images, so the viewer is the author.
  const pageConfig = usePageConfig()
  const currentUsername = pageConfig?.currentUserUsername
  const currentAvatarUrl = pageConfig?.currentUserAvatarUrl ?? null
  const listedImage = useMemo(() => {
    const image = toModalImage(listedAttributes)
    if (!image || !currentUsername) return image

    return { ...image, user: { username: currentUsername, avatar_url: currentAvatarUrl } }
  }, [listedAttributes, currentUsername, currentAvatarUrl])

  const modalImage = loadedImage ?? listedImage

  const editorImage = useMemo(
    () => (modalImage?.assetId ? { id: modalImage.assetId, url: modalImage.src, alt: modalImage.title } : null),
    [modalImage],
  )

  // No session id, as from the library card: the editor's first edit starts its own
  // session, which also keeps a visitor's edits out of the owner's session. The run id goes
  // along only for the viewer's own image, read off the author's username (the payload names
  // no author id), so the editor offers no Delete on another user's run. A community image
  // never carries it, as from the gallery's Edit.
  const isOwnImage = Boolean(currentUsername) && attributes?.user?.username === currentUsername
  const editorRunId = !gallerySlug && isOwnImage ? runId : null

  useEffect(() => {
    if (isEditRequested && editorImage) {
      openFullImageEditor({ image: editorImage, runId: editorRunId })
    }
  }, [isEditRequested, editorImage, editorRunId])

  const handleClose = useCallback(() => {
    navigate(STUDIO_PATH.IMAGE_GENERATOR)
  }, [navigate])

  const handleSelect = useCallback((image) => {
    if (image?.slug) navigate(buildImagePath(image.slug))
  }, [navigate])

  const handleEdit = useImageEdit(EDIT_OPTIONS)
  const handleDownload = useImageDownload()
  const handleGenerate3d = useImageGenerate3d()
  const { regenerate: handleRegenerate, isRegenerating } = useImageRegenerate()

  if (!imageSlug || isEditRequested || !editorImage) {
    return null
  }

  return (
    <ImageModal
      image={modalImage}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      onPrevious={handlePrevious}
      onNext={handleNext}
      isRegenerating={isRegenerating}
      onSelect={handleSelect}
      onRegenerate={handleRegenerate}
      onEdit={handleEdit}
      onDownload={handleDownload}
      onGenerate3d={handleGenerate3d}
      onClose={handleClose}
    />
  )
}

export default memo(ImageDeepLink)
